import React, { useEffect, useState } from 'react';
import {
  Table, Input, Button, Tag, Space, Avatar, Tabs, Modal, Form, InputNumber, message, Typography, DatePicker,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import request from '../utils/request';

const { RangePicker } = DatePicker;

const { Text } = Typography;

const fmtTime = (v?: string) => (v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-');
const money = (v: any) => `¥${Number(v ?? 0).toFixed(2)}`;

// 佣金账户列表
const AccountsTab: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const [current, setCurrent] = useState<any>(null);
  const [records, setRecords] = useState<any[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchList = async (p = page, kw = keyword) => {
    setLoading(true);
    try {
      const res: any = await request.get('/distribution/admin/accounts', {
        params: { page: p, pageSize: 10, keyword: kw || undefined },
      });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page]);

  const fetchRecords = async (userId: number) => {
    setRecordsLoading(true);
    try {
      const res: any = await request.get('/distribution/admin/records', {
        params: { page: 1, pageSize: 20, userId },
      });
      setRecords(res.list || []);
    } finally {
      setRecordsLoading(false);
    }
  };

  const openSettle = (record: any) => {
    form.resetFields();
    setCurrent(record);
    setSettleOpen(true);
    fetchRecords(record.userId);
  };

  const handleSettle = async () => {
    const values = await form.validateFields();
    if (Number(values.amount) > Number(current.total)) {
      message.warning('核销金额不能大于当前余额');
      return;
    }
    setSubmitting(true);
    try {
      await request.post(`/distribution/admin/accounts/${current.userId}/settle`, {
        amount: values.amount,
        note: values.note.trim(),
      });
      message.success('核销成功');
      setSettleOpen(false);
      setCurrent(null);
      fetchList();
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: '用户', width: 80, render: (_: any, r: any) => `#${r.userId}` },
    {
      title: '昵称/姓名', width: 200,
      render: (_: any, r: any) => (
        <Space size={4}>
          <Avatar size="small" icon={<UserOutlined />} />
          {r.user?.realName || r.user?.nickname || '-'}
          {r.user?.phone && <Text type="secondary">({r.user.phone})</Text>}
        </Space>
      ),
    },
    {
      title: '当前余额', dataIndex: 'total', width: 110,
      render: (v: any) => <Tag color="gold" style={{ fontSize: 13 }}>{money(v)}</Tag>,
    },
    { title: '累计佣金', dataIndex: 'totalEarned', width: 110, render: (v: any) => money(v) },
    { title: '累计核销', dataIndex: 'totalSettled', width: 110, render: (v: any) => money(v) },
    { title: '最近变动', dataIndex: 'updatedAt', width: 160, render: (v: string) => fmtTime(v) },
    {
      title: '操作', width: 110, fixed: 'right' as const,
      render: (_: any, r: any) => (
        <Button size="small" type="primary" disabled={Number(r.total) <= 0} onClick={() => openSettle(r)}>
          核销
        </Button>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="搜索昵称/姓名/手机号"
          style={{ width: 240 }}
          onSearch={(v) => { setKeyword(v); setPage(1); fetchList(1, v); }}
        />
      </div>
      <Table columns={columns} dataSource={list} rowKey="id" loading={loading} scroll={{ x: 900 }}
        pagination={{ current: page, total, pageSize: 10, onChange: setPage }} />

      <Modal
        title={`佣金核销 - ${current?.user?.realName || current?.user?.nickname || `用户#${current?.userId}`}`}
        open={settleOpen}
        onCancel={() => { setSettleOpen(false); setCurrent(null); }}
        onOk={handleSettle}
        confirmLoading={submitting}
        okText="确认核销"
        width={600}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          当前余额：<Tag color="gold" style={{ fontSize: 14 }}>{money(current?.total)}</Tag>
          <Text type="secondary">线下转账后在此扣减佣金余额，将生成一条负数流水。</Text>
        </div>
        <Form form={form} layout="vertical">
          <Form.Item name="amount" label="核销金额" rules={[{ required: true, message: '请输入核销金额' }]}>
            <InputNumber min={0.01} precision={2} step={1} placeholder="如 100.00" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="note" label="核销备注" rules={[{ required: true, message: '请填写核销备注' }]}>
            <Input.TextArea rows={2} placeholder="如：微信转账 / 银行打款单号" maxLength={100} />
          </Form.Item>
        </Form>

        <div style={{ fontWeight: 600, margin: '8px 0' }}>最近佣金流水</div>
        <Table
          size="small"
          loading={recordsLoading}
          dataSource={records}
          rowKey="id"
          pagination={false}
          scroll={{ y: 220 }}
          columns={[
            {
              title: '金额', dataIndex: 'amount', width: 100,
              render: (v: any) => (
                <span style={{ color: Number(v) > 0 ? '#065F46' : '#B45309', fontWeight: 600 }}>
                  {Number(v) > 0 ? '+' : ''}{money(v)}
                </span>
              ),
            },
            { title: '说明', dataIndex: 'reason', ellipsis: true },
            { title: '时间', dataIndex: 'createdAt', width: 140, render: (v: string) => fmtTime(v) },
          ]}
        />
      </Modal>
    </>
  );
};

// 佣金流水列表
const RecordsTab: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [kwInput, setKwInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [range, setRange] = useState<any>(null);

  const fetchList = async (p = page) => {
    setLoading(true);
    try {
      const res: any = await request.get('/distribution/admin/records', {
        params: {
          page: p,
          pageSize,
          keyword: keyword || undefined,
          startDate: range?.[0]?.format('YYYY-MM-DD'),
          endDate: range?.[1]?.format('YYYY-MM-DD'),
        },
      });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page, keyword, range, pageSize]);

  const resetFilters = () => {
    setKwInput(''); setKeyword(''); setRange(null); setPage(1);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '分销员', width: 180,
      render: (_: any, r: any) => {
        const u = r.account?.user;
        return u ? (
          <Space size={4}>
            <Avatar size="small" icon={<UserOutlined />} />
            {u.realName || u.nickname || `#${u.id}`}
          </Space>
        ) : `#${r.userId}`;
      },
    },
    {
      title: '金额', dataIndex: 'amount', width: 110,
      render: (v: any) => (
        <span style={{ color: Number(v) > 0 ? '#065F46' : '#B45309', fontWeight: 600 }}>
          {Number(v) > 0 ? '+' : ''}{money(v)}
        </span>
      ),
    },
    { title: '关联订单', dataIndex: 'orderNo', width: 180, render: (v: string) => v || '-' },
    { title: '说明', dataIndex: 'reason', ellipsis: true },
    {
      title: '操作人', width: 120,
      render: (_: any, r: any) => (r.operator ? (r.operator.name || r.operator.username) : <Text type="secondary">系统</Text>),
    },
    { title: '时间', dataIndex: 'createdAt', width: 160, render: (v: string) => fmtTime(v) },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Space>
          <Input.Search
            allowClear
            placeholder="搜索分销员昵称/姓名/手机号"
            style={{ width: 240 }}
            value={kwInput}
            onChange={(e) => setKwInput(e.target.value)}
            onSearch={(v) => { setPage(1); setKeyword(v.trim()); }}
          />
          <RangePicker
            placeholder={['流水起', '流水止']}
            onChange={(v) => { setPage(1); setRange(v); }}
          />
          {(keyword || range) && <Button onClick={resetFilters}>重置</Button>}
        </Space>
      </div>
      <Table columns={columns} dataSource={list} rowKey="id" loading={loading} scroll={{ x: 1000 }}
        pagination={{
          current: page, total, pageSize, showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }} />
    </>
  );
};

// 佣金管理：账户余额 + 线下核销 + 全量流水
const Commissions: React.FC = () => {
  return (
    <div>
      <h2 style={{ margin: '0 0 16px' }}>佣金管理</h2>
      <Tabs
        defaultActiveKey="accounts"
        items={[
          { key: 'accounts', label: '佣金账户', children: <AccountsTab /> },
          { key: 'records', label: '佣金流水', children: <RecordsTab /> },
        ]}
      />
    </div>
  );
};

export default Commissions;
